import { Show, createSignal } from "solid-js";
import type { Accessor } from "solid-js";
import HubbleImage from "../hubble_image/hubble_image";
import { DynamicSvgOverlay } from "./dynamic_svg_overlay";
import { getOverlayDiscount, isOverlayBanner, type BannerV3 } from "./types";

interface BannerSlideProps {
  banner: BannerV3;
  index: number;
  totalBanners: number;
  isDesktop: Accessor<boolean>;
  onBannerClick: (banner: BannerV3) => void;
}

export function BannerSlide(props: BannerSlideProps) {
  const [imageLoaded, setImageLoaded] = createSignal(false);

  const bgUrl = () =>
    props.isDesktop() ? props.banner.desktopImageUrl : props.banner.imageUrl;

  const overlayBanner = () =>
    isOverlayBanner(props.banner) ? props.banner : null;

  const rawSvg = () => {
    const banner = overlayBanner();
    if (!banner) return null;
    return props.isDesktop() ? banner.desktopOverlaySvg : banner.overlaySvg;
  };

  const isClickable = () => {
    switch (props.banner.type) {
      case "BRAND":
        return true;
      case "GIFTING_TEMPLATE":
        return true;
      case "CATEGORY":
        return true;
      case "CAMPAIGN":
        return !!props.banner.externalLink;
      default:
        return false;
    }
  };

  return (
    <Show when={bgUrl()} keyed>
      {(url) => (
        <div
          class="w-full shrink-0"
          classList={{ "cursor-pointer": isClickable() }}
          data-testid="banner-slide"
          data-banner-type={props.banner.type}
          data-banner-slide-index={props.index}
          onClick={() => isClickable() && props.onBannerClick(props.banner)}
        >
          <div class="relative w-full overflow-hidden rounded-2xl">
            <HubbleImage
              alt={`Banner ${(props.index % props.totalBanners) + 1}`}
              src={url}
              class="aspect-[5/2] w-full object-cover md:aspect-[11/2]"
              onLoad={() => setImageLoaded(true)}
            />
            <Show when={imageLoaded() && overlayBanner()} keyed>
              {(banner) => (
                <DynamicSvgOverlay
                  rawSvg={rawSvg()}
                  value={getOverlayDiscount(banner)}
                />
              )}
            </Show>
          </div>
        </div>
      )}
    </Show>
  );
}
